import React from 'react'
import { FormattedMessage } from 'react-intl'
import { Toggle } from '../../legacy-design-system/toggle'
import { Checkbox } from '../../legacy-design-system/checkbox'
import { theme as deprecatedTheme } from '../../styled/theme'
import { theme } from '@contrast-security-inc/design-system-foundations'
import { css, cx } from '@emotion/css'
import { bodyCss, readOnlyGridCss } from '../../legacy-design-system/text'
import { MultiSelectCombobox } from '../../legacy-design-system/combobox'
import { I18nConsumer } from '../i18n-provider'
import { WithTooltip } from '../../design-system'
import { RouteContext } from '../route-provider'
import { ViewerLoader } from '../viewer'
import {
  DigestSettings,
  OrganizationDigestSettingsQuery,
  UserDigestSettingsQuery,
  UpdateOrganizationDigestSettingsMutation,
  UpdateUserDigestSettingsMutation
} from './digest-operations'

export function OrganizationPolicyViolationsRow() {
  return <PolicyViolationsRow />
}

export function UserPolicyViolationsRow() {
  return <PolicyViolationsRow isUserAccount />
}

interface PolicyViolationsRowProps {
  readonly isUserAccount?: boolean
}

export function PolicyViolationsRow({
  isUserAccount = false
}: PolicyViolationsRowProps) {
  return (
    <div
      className={css`
        display: flex;
        flex-direction: row;
        align-items: stretch;
        border-top: 1px solid ${theme.color.neutral30};
      `}
    >
      <div
        className={css`
          display: flex;
          flex-direction: column;
          width: 66.666666%;
          padding: 15px 15px 0px 15px;
          border-right: ${isUserAccount ? '0' : '1px'} solid
            ${theme.color.neutral30};
        `}
      >
        <div
          className={css`
            display: flex;
            flex-direction: row;
            align-items: center;
          `}
        >
          <FormattedMessage id="title_policyViolations">
            {(policyViolationsTitle) => {
              return (
                <React.Fragment>
                  <span className={bodyCss}>{policyViolationsTitle}</span>
                  <WithTooltip
                    placement="right"
                    renderTooltipContent={() => (
                      <div
                        className={css`
                          max-width: 260px;
                        `}
                      >
                        <div
                          className={css`
                            font-weight: ${deprecatedTheme.fontWeights.secondary
                              .boldest};
                            line-height: 2;
                            padding-bottom: 4px;
                          `}
                        >
                          {policyViolationsTitle}
                        </div>
                        <FormattedMessage id="detail_notifications_policiesAppliesToAccess">
                          {(label) => {
                            return <div>{label}</div>
                          }}
                        </FormattedMessage>
                      </div>
                    )}
                  >
                    {() => (
                      <span
                        className={css`
                          margin-left: 3px;
                          color: ${theme.color.turquoise70};
                          font-size: 17px;
                          font-family: ${deprecatedTheme.fontFamilies
                            .secondary};
                          font-weight: ${deprecatedTheme.fontWeights.primary
                            .bold};
                        `}
                      >
                        *
                      </span>
                    )}
                  </WithTooltip>
                </React.Fragment>
              )
            }}
          </FormattedMessage>
        </div>

        <FormattedMessage id="detail_notifications_policiesInViolation">
          {(label) => {
            return <div className={readOnlyGridCss}>{label}</div>
          }}
        </FormattedMessage>
        <DigestSettingsForm isUserAccount={isUserAccount} />
      </div>

      <DisabledToggleWithTooltip showBorder />
      <DisabledToggleWithTooltip />
    </div>
  )
}

interface DisabledToggleWithTooltipProps {
  readonly showBorder?: boolean
}

function DisabledToggleWithTooltip({
  showBorder
}: DisabledToggleWithTooltipProps) {
  return (
    <div
      className={css`
        display: flex;
        flex-direction: column;
        background-color: ${theme.color.white};
        width: 16.666666%;
        justify-content: center;
        align-items: center;
        border-right: ${!!showBorder ? '1px' : '0'} solid
          ${theme.color.neutral30};
      `}
    >
      <WithTooltip
        renderTooltipContent={() => (
          <FormattedMessage id="detail_notifications_policyViolationsRequiredTooltip">
            {(label) => {
              return (
                <div
                  className={css`
                    max-width: 260px;
                  `}
                >
                  {label}
                </div>
              )
            }}
          </FormattedMessage>
        )}
      >
        {() => {
          return <Toggle checked={true} onChange={() => ''} disabled={true} />
        }}
      </WithTooltip>
    </div>
  )
}

interface DigestSettingsFormProps {
  readonly isUserAccount?: boolean
}

function DigestSettingsForm({
  isUserAccount = false
}: DigestSettingsFormProps) {
  return (
    <RouteContext.Consumer>
      {({ organizationID }) => {
        if (!organizationID) {
          throw new Error('organizationID is not defined')
        }

        if (isUserAccount) {
          return (
            <ViewerLoader>
              {({ loading, viewer }) => {
                return (
                  !loading &&
                  !!viewer && (
                    <UserDigestSettingsQuery
                      organizationID={organizationID}
                      viewerID={viewer.id}
                    >
                      {(digestSettings) => (
                        <UpdateUserDigestSettingsMutation>
                          {(updateUserDigestSettings) => (
                            <DigestSettingsFieldsWrapper
                              digestSettings={digestSettings}
                              updateDigestSettings={(settings) =>
                                updateUserDigestSettings({
                                  ...settings,
                                  viewerID: viewer.id,
                                  organizationID
                                })
                              }
                            />
                          )}
                        </UpdateUserDigestSettingsMutation>
                      )}
                    </UserDigestSettingsQuery>
                  )
                )
              }}
            </ViewerLoader>
          )
        }

        return (
          <OrganizationDigestSettingsQuery organizationID={organizationID}>
            {(digestSettings) => (
              <UpdateOrganizationDigestSettingsMutation>
                {(updateOrganizationDigestSettings) => (
                  <DigestSettingsFieldsWrapper
                    digestSettings={digestSettings}
                    updateDigestSettings={(settings) =>
                      updateOrganizationDigestSettings({
                        ...settings,
                        organizationID
                      })
                    }
                  />
                )}
              </UpdateOrganizationDigestSettingsMutation>
            )}
          </OrganizationDigestSettingsQuery>
        )
      }}
    </RouteContext.Consumer>
  )
}

interface Digest {
  readonly id: string
  readonly i18nID: string
}

const allDigests: Digest[] = [
  { id: 'compliance', i18nID: 'title_compliance' },
  { id: 'library', i18nID: 'title_library' },
  { id: 'remediation', i18nID: 'title_remediation' }
]

const digestEnabledPredicate = ({
  digests,
  expectedID
}: {
  readonly digests: Digest[]
  readonly expectedID: string
}) => digests.length === 0 || digests.some(({ id }) => id === expectedID)

type UpdateDigestSettings = (args: DigestSettings) => Promise<any>

interface WrapperProps {
  readonly digestSettings: DigestSettings
  readonly updateDigestSettings: UpdateDigestSettings
}

export function DigestSettingsFieldsWrapper({
  digestSettings: { compliance, library, remediation },
  updateDigestSettings
}: WrapperProps) {
  const digests = allDigests.filter(
    ({ id }) =>
      (id === 'compliance' && compliance) ||
      (id === 'library' && library) ||
      (id === 'remediation' && remediation)
  )

  return (
    <DigestSettingsFields
      digestsEnabled={digests.length > 0}
      setDigestsEnabled={(enabled) =>
        updateDigestSettings({
          compliance: enabled,
          remediation: enabled,
          library: enabled
        })
      }
      digests={digests.length === allDigests.length ? [] : digests}
      setDigests={(digests) =>
        updateDigestSettings({
          compliance: digestEnabledPredicate({
            digests,
            expectedID: 'compliance'
          }),
          remediation: digestEnabledPredicate({
            digests,
            expectedID: 'remediation'
          }),
          library: digestEnabledPredicate({
            digests,
            expectedID: 'library'
          })
        })
      }
    />
  )
}

interface DigestSettingsFieldsProps {
  readonly digestsEnabled: boolean
  readonly setDigestsEnabled: (enabled: boolean) => void
  readonly digests: Digest[]
  readonly setDigests: (digests: Digest[]) => void
}

export const digestSelectionTestID = 'digest-selecion-test-id'

function DigestSettingsFields({
  digestsEnabled,
  setDigestsEnabled,
  digests,
  setDigests
}: DigestSettingsFieldsProps) {
  return (
    <I18nConsumer>
      {({ intl }) => (
        <div
          className={css`
            margin-top: 20px;
          `}
        >
          <FormattedMessage id="detail_notifications_aggregatePolicyViolations">
            {(label) => {
              return (
                <Checkbox
                  id="organization_notifications_digest"
                  label={label as any}
                  value={digestsEnabled}
                  onChange={() => setDigestsEnabled(!digestsEnabled)}
                />
              )
            }}
          </FormattedMessage>
          {digestsEnabled && (
            <div
              data-testid={digestSelectionTestID}
              className={cx(
                bodyCss,
                css`
                  margin-left: 26px;
                  max-width: 324px;
                `
              )}
            >
              <MultiSelectCombobox<Digest>
                id="organization_notifications_digests"
                renderLabel={() => (
                  <FormattedMessage id="detail_notifications_choosePoliciesToAggregate">
                    {(label) => {
                      return (
                        <div
                          className={cx(
                            readOnlyGridCss,
                            css`
                              padding-bottom: 4px;
                            `
                          )}
                        >
                          {label}
                        </div>
                      )
                    }}
                  </FormattedMessage>
                )}
                items={[
                  {
                    items: allDigests
                  }
                ]}
                onChange={setDigests}
                values={digests}
                placeHolderTag={intl.formatMessage({ id: 'detail_all' })}
                itemToID={({ id }) => id}
                itemToString={({ i18nID }) =>
                  intl.formatMessage({
                    id: i18nID
                  })
                }
              />
            </div>
          )}
        </div>
      )}
    </I18nConsumer>
  )
}
